import { useState, useEffect, useRef } from "react";
import { useAuth, useUser } from "@clerk/react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { DisplayMode, SourceSnapshot } from "@tuit/shared-types";
import { TopBar } from "../components/TopBar.js";
import { SourcePanel } from "../components/SourcePanel.js";
import { adminCatalogQueue, adminCatalogPatch, adminCatalogFeed, adminCatalogFeedRaw, adminCatalogFavicon } from "../api.js";
import { getFaviconUrl } from "../utils/favicon.js";
import { FlatView } from "../components/FlatView.js";
import { PhoneFrame } from "../components/PhoneFrame.js";
import { PHONE_MODELS } from "../utils/phoneModels.js";
import type { PhoneModel } from "../utils/phoneModels.js";
import styles from "../styles/pages/AdminCatalogReviewPage.module.css";

interface CatalogSource {
  id: string;
  title: string;
  feedUrl: string;
  htmlUrl: string | null;
  faviconUrl: string | null;
  description: string | null;
  category: string;
  status: "pending" | "approved" | "rejected";
  thumbnails: boolean;
  language: string;
  tags: string[];
  iconBackground: string | null;
}

const ICON_BG_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "Transparent", value: "" },
  { label: "White",       value: "#ffffff" },
  { label: "87% white",   value: "#dedede" },
  { label: "75% white",   value: "#bfbfbf" },
  { label: "62% white",   value: "#9e9e9e" },
  { label: "50% grey",    value: "#808080" },
  { label: "37% black",   value: "#5e5e5e" },
  { label: "25% black",   value: "#404040" },
  { label: "Black",       value: "#000000" },
];

interface FeedItem {
  title: string;
  link: string | null;
  pubDate: string | null;
  summary: string | null;
  thumbnail: string | null;
}

type PreviewMode = "post" | "thumbnail" | "gallery" | "picture" | "json" | "raw-json";

const PREVIEW_MODES: { mode: PreviewMode; label: string }[] = [
  { mode: "post",      label: "Post" },
  { mode: "thumbnail", label: "Thumbnail" },
  { mode: "gallery",   label: "Gallery" },
  { mode: "picture",   label: "Picture Set" },
];

function toSnapshot(
  items: FeedItem[],
  sourceId: string,
  titleStr: string,
  stripThumbs: boolean,
  faviconUrl?: string | null,
  iconBackground?: string,
): SourceSnapshot {
  return {
    sourceId,
    title: titleStr,
    fetchedAt: new Date().toISOString(),
    fetchStatus: "ok",
    errorMessage: null,
    faviconUrl: faviconUrl || null,
    iconBackground: iconBackground || undefined,
    items: items.map((item, i) => {
      let domain = "";
      try { domain = new URL(item.link ?? "").hostname.replace(/^www\./, ""); } catch {}
      return {
        id: `preview-${i}`,
        title: item.title,
        url: item.link ?? "#",
        domain,
        author: null,
        score: null,
        publishedAt: item.pubDate ?? new Date().toISOString(),
        thumbnail: stripThumbs ? null : item.thumbnail,
        duplicateOf: null,
      };
    }),
  };
}

const EMPTY_SET = new Set<string>();
const NOOP = () => {};

function htmlEscape(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function syntaxHighlight(json: string): string {
  return htmlEscape(json).replace(
    /("(?:\\u[0-9a-fA-F]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      const isKey = /^".*"\s*:$/.test(match);
      const isString = match.startsWith('"') && !isKey;
      const isBool = match === "true" || match === "false";
      const isNull = match === "null";
      const color = isKey ? "#93c5fd" : isString ? "#86efac" : isBool ? "#f9a8d4" : isNull ? "#94a3b8" : "#fca5a5";
      return `<span style="color:${color}">${match}</span>`;
    }
  );
}

const ALL_TAGS = [
  { id: "nsfw",        label: "NSFW",        group: "Content" },
  { id: "satire",      label: "Satire",       group: "Content" },
  { id: "opinion",     label: "Opinion",      group: "Content" },
  { id: "longform",    label: "Longform",     group: "Content" },
  { id: "paywall",     label: "Paywall",      group: "Content" },
  { id: "video",       label: "Video",        group: "Format" },
  { id: "podcast",     label: "Podcast",      group: "Format" },
  { id: "newsletter",  label: "Newsletter",   group: "Format" },
  { id: "aggregator",  label: "Aggregator",   group: "Format" },
  { id: "official",    label: "Official",     group: "Source" },
  { id: "community",   label: "Community",    group: "Source" },
  { id: "academic",    label: "Academic",     group: "Source" },
];


export function AdminCatalogReviewPage() {
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;

  const [queue, setQueue] = useState<CatalogSource[]>([]);
  const [source, setSource] = useState<CatalogSource | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [empty, setEmpty] = useState(false);
  const [saving, setSaving] = useState(false);
  const reviewedIdsRef = useRef<string[]>([]);

  // Form state
  const [title, setTitle] = useState("");
  const [feedUrl, setFeedUrl] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [htmlUrl, setHtmlUrl] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [language, setLanguage] = useState("en");
  const [thumbnails, setThumbnails] = useState(false);
  const [tags, setTags] = useState<string[]>([]);

  const [iconBackground, setIconBackground] = useState("");
  const [faviconFetching, setFaviconFetching] = useState(false);
  const [homepageFetching, setHomepageFetching] = useState(false);

  // Feed preview
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [feedMeta, setFeedMeta] = useState<{ feedTitle: string | null; feedLink: string | null; feedDescription: string | null } | null>(null);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [rawFeedData, setRawFeedData] = useState<unknown>(null);
  const [rawFeedLoading, setRawFeedLoading] = useState(false);
  const [rawFeedError, setRawFeedError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<PreviewMode>("thumbnail");
  const [previewCols, setPreviewCols] = useState(2);
  const [previewMobile, setPreviewMobile] = useState(false);
  const [phoneModel, setPhoneModel] = useState<PhoneModel>(PHONE_MODELS[0]!);

  if (isLoaded && (!userId || (role !== "admin" && role !== "editor"))) {
    navigate({ to: "/" });
    return null;
  }

  async function fetchFavicon(targetUrl: string) {
    setFaviconFetching(true);
    try {
      const data = await adminCatalogFavicon(targetUrl);
      if (data.faviconUrl) setFaviconUrl(data.faviconUrl);
    } catch {
      // silently ignore — field stays empty
    } finally {
      setFaviconFetching(false);
    }
  }

  async function fetchHomepage(targetUrl: string) {
    setHomepageFetching(true);
    try {
      const data = await adminCatalogFavicon(targetUrl);
      if (data.faviconUrl) setFaviconUrl(data.faviconUrl);
      if (data.title) setTitle(data.title);
    } catch {
      // silently ignore
    } finally {
      setHomepageFetching(false);
    }
  }

  function fetchFeed(feedUrl: string, onData?: (data: { feedLink: string | null; feedDescription: string | null }) => void) {
    setFeedItems([]);
    setFeedMeta(null);
    setFeedError(null);
    setFeedLoading(true);
    adminCatalogFeed(feedUrl)
      .then((data) => {
        setFeedItems(data.items);
        setFeedMeta({ feedTitle: data.feedTitle, feedLink: data.feedLink, feedDescription: data.feedDescription });
        onData?.(data);
      })
      .catch((err) => setFeedError(String(err)))
      .finally(() => setFeedLoading(false));
  }

  function fetchRawFeed(feedUrl: string) {
    setRawFeedData(null);
    setRawFeedError(null);
    setRawFeedLoading(true);
    adminCatalogFeedRaw(feedUrl)
      .then((data) => setRawFeedData(data))
      .catch((err) => setRawFeedError(String(err)))
      .finally(() => setRawFeedLoading(false));
  }

  function applySource(s: CatalogSource) {
    setSource(s);
    setRawFeedData(null);
    setRawFeedError(null);
    setTitle(s.title);
    setFeedUrl(s.feedUrl);
    setCategory(s.category);
    setDescription(s.description ?? "");
    setHtmlUrl(s.htmlUrl ?? "");
    setFaviconUrl(s.faviconUrl ?? "");
    setIconBackground(s.iconBackground ?? "");
    setLanguage(s.language);
    setThumbnails(s.thumbnails);
    setTags(s.tags ?? []);
    fetchFeed(s.feedUrl, (data) => {
      if (!s.htmlUrl && data.feedLink) setHtmlUrl(data.feedLink);
      if (!s.description && data.feedDescription) setDescription(data.feedDescription);
    });
    if (!s.faviconUrl) {
      fetchFavicon(s.htmlUrl || s.feedUrl);
    }
  }

  async function refillQueue(currentQueue: CatalogSource[]) {
    const needed = 10 - currentQueue.length;
    if (needed <= 0) return;
    const excludeIds = [...currentQueue.map((s) => s.id), ...reviewedIdsRef.current];
    try {
      const data = await adminCatalogQueue(excludeIds, needed);
      const fresh = data.sources as CatalogSource[];
      if (fresh.length > 0) setQueue((prev) => [...prev, ...fresh]);
      setRemaining(data.remaining);
    } catch {
      // silently fail — queue just won't grow
    }
  }

  function advanceQueue(removedId: string, currentQueue: CatalogSource[]) {
    const newQueue = currentQueue.filter((s) => s.id !== removedId);
    setQueue(newQueue);
    if (newQueue.length === 0) {
      setEmpty(true);
      setSource(null);
    } else {
      const oldIdx = currentQueue.findIndex((s) => s.id === removedId);
      const next = newQueue[oldIdx] ?? newQueue[newQueue.length - 1]!;
      applySource(next);
    }
    if (newQueue.length < 4) refillQueue(newQueue);
  }

  const initQueue = async () => {
    setLoading(true);
    try {
      const data = await adminCatalogQueue([], 10);
      const sources = data.sources as CatalogSource[];
      setQueue(sources);
      setRemaining(data.remaining);
      if (sources.length === 0) {
        setEmpty(true);
      } else {
        applySource(sources[0]!);
      }
    } catch {
      setEmpty(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isLoaded && userId) initQueue(); }, [isLoaded, userId]);

  async function handleApprove() {
    if (!source || saving) return;
    setSaving(true);
    try {
      await adminCatalogPatch(source.id, { title, feedUrl: feedUrl || undefined, category, description: description || null, htmlUrl: htmlUrl || null, faviconUrl: faviconUrl || null, iconBackground: iconBackground || null, language, thumbnails, tags, status: "approved" });
      reviewedIdsRef.current.push(source.id);
      advanceQueue(source.id, queue);
    } finally {
      setSaving(false);
    }
  }

  async function handleReject() {
    if (!source || saving) return;
    setSaving(true);
    try {
      await adminCatalogPatch(source.id, { status: "rejected" });
      reviewedIdsRef.current.push(source.id);
      advanceQueue(source.id, queue);
    } finally {
      setSaving(false);
    }
  }

  function handleSkip() {
    if (!source || queue.length <= 1) return;
    const rest = queue.filter((s) => s.id !== source.id);
    setQueue([...rest, source]);
    applySource(rest[0]!);
  }

  function toggleTag(tag: string) {
    setTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  }

  if (loading && !source) {
    return (
      <div className={styles.page}>
        <TopBar />
        <div className={styles.loadingState}>Loading…</div>
      </div>
    );
  }

  if (empty) {
    return (
      <div className={styles.page}>
        <TopBar />
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>Queue empty</p>
          <p className={styles.emptyHint}>No pending sources to review.</p>
          <div className={styles.emptyActions}>
            <Link to="/admin/catalog/import" className={styles.primaryBtn}>Import more →</Link>
            <Link to="/admin" className={styles.ghostBtn}>← Admin</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.layout}>
        {/* Left: form */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <Link to="/admin" className={styles.back}>← Admin</Link>
            <span className={styles.remainingBadge}>{remaining} remaining</span>
          </div>

          <div className={styles.form}>
            <div className={styles.sourceUrl}>
              <span className={styles.sourceKey}>{source?.id}</span>
              <div className={styles.feedUrlRow}>
                <input
                  className={styles.input}
                  type="url"
                  value={feedUrl}
                  onChange={(e) => setFeedUrl(e.target.value)}
                  style={{ flex: 1, fontSize: 11 }}
                />
                <button
                  type="button"
                  className={styles.fetchBtn}
                  onClick={() => fetchFeed(feedUrl)}
                  disabled={feedLoading || !feedUrl}
                  title="Re-fetch with this URL"
                >
                  {feedLoading ? "…" : "↻"}
                </button>
                <a href={feedUrl} target="_blank" rel="noopener noreferrer" className={styles.feedUrlLink} title="Open feed URL">↗</a>
              </div>
            </div>

            <div className={styles.label}>
              Homepage URL
              <div className={styles.faviconRow}>
                <input className={styles.input} type="url" value={htmlUrl} onChange={(e) => setHtmlUrl(e.target.value)} style={{ flex: 1 }} />
                <button
                  type="button"
                  className={styles.fetchBtn}
                  onClick={() => fetchHomepage(htmlUrl)}
                  disabled={homepageFetching || !htmlUrl}
                  title="Fetch title and favicon from homepage"
                >
                  {homepageFetching ? "…" : "⇒"}
                </button>
              </div>
            </div>

            <label className={styles.label}>
              Title
              <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>

            <label className={styles.label}>
              Category
              <input className={styles.input} value={category} onChange={(e) => setCategory(e.target.value)} />
            </label>

            <label className={styles.label}>
              Description
              <textarea className={styles.textarea} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>

            <div className={styles.label}>
              Favicon URL
              <div className={styles.faviconRow}>
                <input className={styles.input} type="url" value={faviconUrl} onChange={(e) => setFaviconUrl(e.target.value)} placeholder={faviconFetching ? "Fetching…" : "Auto-fetch or paste URL"} disabled={faviconFetching} style={{ flex: 1 }} />
                <button
                  type="button"
                  className={styles.fetchBtn}
                  onClick={() => fetchFavicon(htmlUrl || source?.feedUrl || "")}
                  disabled={faviconFetching || (!htmlUrl && !source?.feedUrl)}
                >
                  {faviconFetching ? "…" : "Fetch"}
                </button>
              </div>
            </div>

            <div className={styles.label}>
              Icon BG
              <div className={styles.iconBgSwatches}>
                {ICON_BG_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    title={opt.label}
                    className={`${styles.iconBgSwatch} ${iconBackground === opt.value ? styles.iconBgSwatchActive : ""}`}
                    style={opt.value ? { background: opt.value } : undefined}
                    onClick={() => setIconBackground(opt.value)}
                  >
                    {!opt.value && <span className={styles.iconBgTransparentMark}>✕</span>}
                  </button>
                ))}
              </div>
            </div>

            <label className={styles.label}>
              Language
              <input className={styles.input} value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="en" style={{ width: 80 }} />
            </label>

            <label className={styles.checkLabel}>
              <input type="checkbox" checked={thumbnails} onChange={(e) => setThumbnails(e.target.checked)} />
              Show thumbnails
            </label>

            <div className={styles.tagSection}>
              <span className={styles.tagSectionLabel}>Tags</span>
              <div className={styles.tagGrid}>
                {ALL_TAGS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`${styles.tagBtn} ${tags.includes(t.id) ? styles.tagBtnActive : ""}`}
                    onClick={() => toggleTag(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {queue.length > 0 && (
            <div className={styles.queueSection}>
              <span className={styles.queueLabel}>In queue — {queue.length}</span>
              <div className={styles.queueList}>
                {queue.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`${styles.queueItem} ${s.id === source?.id ? styles.queueItemActive : ""}`}
                    onClick={() => applySource(s)}
                  >
                    <span className={styles.queueItemTitle}>{s.title || s.feedUrl}</span>
                    {s.category && <span className={styles.queueItemCat}>{s.category}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={styles.footer}>
            <button type="button" className={styles.approveBtn} onClick={handleApprove} disabled={saving}>
              {saving ? "…" : "Approve"}
            </button>
            <button type="button" className={styles.rejectBtn} onClick={handleReject} disabled={saving}>
              Reject
            </button>
            <button type="button" className={styles.skipBtn} onClick={handleSkip} disabled={saving}>
              Skip
            </button>
          </div>
        </div>

        {/* Right: feed preview */}
        <div className={styles.preview}>
          <div className={styles.previewHeader}>
            {source && (() => {
              const derivedUrl = (() => { try { return getFaviconUrl(new URL(source.htmlUrl || source.feedUrl).hostname.replace(/^www\./, "")); } catch { return ""; } })();
              const src = faviconUrl || derivedUrl;
              return src ? <img src={src} width={20} height={20} alt="" aria-hidden="true" style={{ flexShrink: 0, borderRadius: 3 }} /> : null;
            })()}
            <span className={styles.previewTitle}>{title}</span>
            <div className={styles.modeToggle}>
              {PREVIEW_MODES.map(({ mode, label }) => (
                <button
                  key={mode}
                  type="button"
                  className={`${styles.modeBtn} ${previewMode === mode ? styles.modeBtnActive : ""}`}
                  onClick={() => setPreviewMode(mode)}
                  disabled={previewMobile || previewMode === "json" || previewMode === "raw-json"}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className={styles.modeToggle}>
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`${styles.modeBtn} ${previewCols === n ? styles.modeBtnActive : ""}`}
                  onClick={() => setPreviewCols(n)}
                  title={`${n} column${n > 1 ? "s" : ""}`}
                  disabled={previewMobile || previewMode === "json" || previewMode === "raw-json"}
                >
                  {n}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`${styles.modeBtn} ${previewMobile ? styles.modeBtnActive : ""}`}
              onClick={() => setPreviewMobile((m) => !m)}
              title="Simulate mobile layout"
              disabled={previewMode === "json" || previewMode === "raw-json"}
            >
              Mobile
            </button>
            <button
              type="button"
              className={`${styles.modeBtn} ${previewMode === "json" ? styles.modeBtnActive : ""}`}
              onClick={() => setPreviewMode((m) => m === "json" ? "thumbnail" : "json")}
              title="View parsed feed JSON"
            >
              JSON
            </button>
            <button
              type="button"
              className={`${styles.modeBtn} ${previewMode === "raw-json" ? styles.modeBtnActive : ""}`}
              onClick={() => {
                if (previewMode === "raw-json") {
                  setPreviewMode("thumbnail");
                } else {
                  setPreviewMode("raw-json");
                  if (!rawFeedData && !rawFeedLoading && source) fetchRawFeed(feedUrl || source.feedUrl);
                }
              }}
              title="View full rss-parser output"
            >
              Raw
            </button>
            {previewMobile && (
              <select
                className={styles.modelSelect}
                value={phoneModel.id}
                onChange={(e) => setPhoneModel(PHONE_MODELS.find((m) => m.id === e.target.value) ?? PHONE_MODELS[0]!)}
              >
                {PHONE_MODELS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              className={styles.modeBtn}
              onClick={() => {
                if (!source) return;
                if (previewMode === "raw-json") fetchRawFeed(feedUrl || source.feedUrl);
                else fetchFeed(feedUrl || source.feedUrl);
              }}
              disabled={feedLoading || rawFeedLoading}
              title="Re-fetch feed"
            >
              {feedLoading || rawFeedLoading ? "…" : "↻"}
            </button>
            {source?.htmlUrl && (
              <a href={source.htmlUrl} target="_blank" rel="noopener noreferrer" className={styles.siteLink}>
                Visit site →
              </a>
            )}
          </div>
          {previewMode === "raw-json" && rawFeedLoading && <p className={styles.previewHint}>Loading raw feed…</p>}
          {previewMode === "raw-json" && rawFeedError && <p className={styles.previewError}>Could not load raw feed: {rawFeedError}</p>}
          {previewMode === "raw-json" && !rawFeedLoading && rawFeedData !== null && (
            <div className={styles.jsonView}>
              <pre
                className={styles.jsonPre}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: syntaxHighlight(JSON.stringify(rawFeedData, null, 2)) }}
              />
            </div>
          )}
          {previewMode !== "raw-json" && feedLoading && <p className={styles.previewHint}>Loading feed…</p>}
          {previewMode !== "raw-json" && feedError && <p className={styles.previewError}>Could not load feed: {feedError}</p>}
          {previewMode !== "raw-json" && !feedLoading && !feedError && feedItems.length === 0 && (
            <p className={styles.previewHint}>No items found.</p>
          )}
          {!feedLoading && feedItems.length > 0 && source && previewMode === "json" && (
            <div className={styles.jsonView}>
              <pre
                className={styles.jsonPre}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: syntaxHighlight(JSON.stringify(
                    {
                      feedUrl: source.feedUrl,
                      channel: feedMeta,
                      itemCount: feedItems.length,
                      items: feedItems,
                    },
                    null,
                    2,
                  )),
                }}
              />
            </div>
          )}
          {!feedLoading && feedItems.length > 0 && source && previewMode !== "json" && previewMode !== "raw-json" && (() => {
            const displayMode: DisplayMode =
              previewMode === "gallery" ? "gallery" :
              previewMode === "picture" ? "picture" : "text";
            const snap = toSnapshot(feedItems, source.id, title, previewMode === "post" || !thumbnails, faviconUrl, iconBackground);
            const colWidths: Record<number, string> = { 1: "100%", 2: "520px", 3: "360px", 4: "270px" };
            if (previewMobile) {
              return (
                <PhoneFrame model={phoneModel}>
                  <FlatView
                    sections={[snap]}
                    readIds={EMPTY_SET}
                    onRead={NOOP}
                    suppressed={EMPTY_SET}
                    mobile={true}
                  />
                </PhoneFrame>
              );
            }
            return (
              <div className={styles.previewPanel} style={{ maxWidth: colWidths[previewCols] }}>
                <SourcePanel section={snap} displayMode={displayMode} />
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
